import React, { useEffect, useRef } from 'react';
import '../../../components/modal/planning-comparison/PlanningComparisonModal.css';
import './AuditoriaCiliaModais.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Lista de peças de um evento (at-05, 16/08/2026).
 *
 * Um componente para QUATRO eventos — importação inicial, peças da solicitação, peças fora
 * da compra e peças devolvidas ao funil — porque todos respondem a mesma pergunta ("quais
 * peças, e o que aconteceu com cada uma?") e mudam só no rótulo e no badge. É o mesmo
 * caminho do `AuditoriaDecisoesModal` do Portal, que serve dois eventos por props.
 */

interface Props {
  itens: Record<string, any>[] | null;
  titulo: string;
  em: string;
  autor: string | null;
  rotuloAutor?: string;
  onFechar: () => void;
}

/** Badge da situação/resultado de cada peça — rótulos iguais aos da tela de conferência. */
function badgeDaPeca(item: Record<string, any>): { rotulo: string; classe: string } | null {
  if (item.resultadoErp === 'CRIADA') return { rotulo: 'Criada no ERP', classe: 'font-added' };
  if (item.resultadoErp === 'SIMULADA') return { rotulo: 'Criada (simulação)', classe: 'font-simulada' };
  if (item.resultadoErp === 'ATUALIZADA') return { rotulo: 'Atualizada no ERP', classe: 'font-qtd' };
  if (item.para === 'EXISTENTE') return { rotulo: 'Encontrada no ERP', classe: 'font-added' };
  if (item.para === 'A_CRIAR') return { rotulo: 'A criar', classe: 'font-reprocessado' };
  if (item.para === 'A_COMPRAR') return { rotulo: 'Voltou para "A Comprar"', classe: 'font-qtd' };

  switch (item.situacao ?? item.situacaoFinal) {
    case 'A_COMPRAR':
      return { rotulo: 'A Comprar', classe: 'font-added' };
    // `EM_ESTOQUE` saiu do sistema em 20/08/2026 (ADR 0006) e não tem mais rótulo: snapshot
    // antigo que a traga cai no `default` e fica sem badge, em vez de exibir o nome de uma
    // situação aposentada como se ela ainda existisse.
    case 'AGUARDANDO_COBERTURA':
      return { rotulo: 'Sob Análise', classe: 'font-reprocessado' };
    case 'ENVIADO':
      return { rotulo: 'Solicitada', classe: 'font-qtd' };
    case 'FORA_DO_FLUXO':
      return { rotulo: 'Fora do Fluxo', classe: 'font-herdado' };
    default:
      return null;
  }
}

function dataHora(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

const AuditoriaItensModal: React.FC<Props> = ({ itens, titulo, em, autor, rotuloAutor = 'Registrado por', onFechar }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cliqueFora = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onFechar();
    };
    document.addEventListener('mousedown', cliqueFora);
    return () => document.removeEventListener('mousedown', cliqueFora);
  }, [onFechar]);

  return (
    <div className="planning-modal-overlay">
      <div className="planning-modal-content" ref={ref} role="dialog" aria-modal="true" aria-label={titulo}>
        <div className="planning-modal-header">
          <h2>{titulo}</h2>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="planning-modal-metadata">
          <div className="metadata-item">
            {rotuloAutor}: {autor || 'Sistema'}
          </div>
          <div className="metadata-item">Data: {dataHora(em)}</div>
        </div>

        <div className="planning-modal-body">
          {!itens ? (
            /*
              Log anterior a esta entrega: o evento existe, o detalhamento não. Some o
              detalhe, nunca o evento — auditoria que esconde registro mente por omissão.
            */
            <div className="planning-modal-fallback">
              <div className="fallback-title">{titulo}</div>
              <div className="fallback-desc">Este registro não contém a lista de peças.</div>
              <div className="fallback-note">
                A lista passou a ser guardada em agosto de 2026; registros anteriores mantêm apenas os
                totais, que continuam visíveis na linha do tempo.
              </div>
            </div>
          ) : itens.length === 0 ? (
            <div className="cilia-diff-vazio">Nenhuma peça neste registro.</div>
          ) : (
            <div className="planning-comparison-grid single-column">
              <div className="planning-column">
                <h3>
                  {itens.length} {itens.length === 1 ? 'peça' : 'peças'}
                </h3>
                <div className="status-flow-list">
                  {itens.map((item, i) => {
                    const badge = badgeDaPeca(item);
                    return (
                      <div key={`${item.codigo}-${i}`} className="status-flow-item item-unchanged">
                        <span className="item-index">{i + 1}</span>
                        <span className="item-label" title={`${item.codigo} — ${item.descricao ?? ''}`}>
                          <strong>{item.codigo}</strong> {item.descricao ?? ''}
                          {/* Dados secundários na MESMA linha (regra de célula de 12/08/2026). */}
                          {item.quantidade != null && <span className="cilia-item-extra">qtd. {item.quantidade}</span>}
                          {item.esteira && <span className="cilia-item-extra">{item.esteira}</span>}
                          {/* Os selos "compra forçada" e "saldo N" saíram em 20/08/2026 (ADR
                              0006): descreviam a regra de estoque, que deixou de existir. */}
                        </span>
                        {badge && <span className={`badge-change ${badge.classe}`}>{badge.rotulo}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditoriaItensModal;
